/**
 * Sistema de consentimiento de cookies — Ley 21.719 Chile.
 *
 * - Persiste decisión en localStorage con versión + timestamp (registro de consentimiento).
 * - Notifica a suscriptores cuando cambia.
 * - Expone helper global `window.__openCookiePreferences()` para el footer.
 *
 * Categorías:
 *   necesarias: SIEMPRE on (sesión, CSRF, preferencias UI). No requieren consentimiento.
 *   analiticas: PostHog (analytics + session recording). Default OFF. Requiere opt-in.
 *   marketing: ninguna activa hoy. Default OFF. Si en el futuro agregás retargeting, requiere opt-in explícito.
 */

const STORAGE_KEY = "ldc_cookie_consent_v1";
const POLICY_VERSION = "2026-02-22";

const DEFAULT_CONSENT = {
  necesarias: true,
  analiticas: false,
  marketing: false,
};

const listeners = new Set();

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Si la versión cambió, requerimos re-consent.
    if (parsed.policyVersion !== POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(categories, source = "banner") {
  const decision = {
    ...DEFAULT_CONSENT,
    ...categories,
    necesarias: true, // siempre on
    policyVersion: POLICY_VERSION,
    decidedAt: new Date().toISOString(),
    source, // "banner_accept_all" | "banner_reject_all" | "banner_custom" | "preferences"
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(decision));
  } catch {
    // localStorage bloqueado (modo privado, etc) → memoria temporal
  }
  listeners.forEach((cb) => {
    try {
      cb(decision);
    } catch {
      // ignorar errores de subs
    }
  });
  return decision;
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorar
  }
  listeners.forEach((cb) => cb(null));
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isConsentDecided() {
  return getConsent() !== null;
}

export function openPreferences() {
  if (typeof window !== "undefined" && typeof window.__openCookiePreferences === "function") {
    window.__openCookiePreferences();
  }
}

export const CONSENT_POLICY_VERSION = POLICY_VERSION;
